/**
 * Утиліти для роботи з адаптивними зображеннями
 */

import config from '../config/config';

export const DEFAULT_RECIPE_IMAGE = 'https://cookly-food.s3.eu-north-1.amazonaws.com/recipes/default/default.webp';
export const DEFAULT_AVATAR = 'https://cookly-food.s3.eu-north-1.amazonaws.com/avatars/default/default.webp';

// Ширини, які гарантовано існують у S3 (100% покриття для всіх рецептів у базі)
const SAFE_SRCSET_WIDTHS = [480, 768];

/**
 * Перетворює S3 ключ або URL в повний S3 URL
 * @param {string} imageUrl - S3 ключ або URL
 * @returns {string} - Повний S3 URL
 */
export const getFullS3Url = (imageUrl) => {
  if (!imageUrl) return DEFAULT_RECIPE_IMAGE;

  let cleanUrl = String(imageUrl).trim();
  if (!cleanUrl || cleanUrl === 'null' || cleanUrl === 'undefined') {
    return DEFAULT_RECIPE_IMAGE;
  }
  if (cleanUrl.includes('default')) {
    return DEFAULT_RECIPE_IMAGE;
  }
  if (cleanUrl.includes('/_resized/')) {
    cleanUrl = cleanUrl.replace('/_resized/', '/');
  } else if (cleanUrl.startsWith('_resized/')) {
    cleanUrl = cleanUrl.replace(/^_resized\//, '');
  }
  cleanUrl = cleanUrl.replace(/\.w\d+(\.[a-z0-9]+)?$/i, (m, ext) => ext || '');

  // Якщо це вже повний URL, повертаємо як є
  if (cleanUrl.startsWith('http')) {
    return cleanUrl;
  }

  // Якщо це S3 ключ, формуємо повний URL
  const bucket = 'cookly-food';
  const region = 'eu-north-1';
  const key = cleanUrl.replace(/^\/+/, '');
  return `https://${bucket}.s3.${region}.amazonaws.com/${key}`;
};

/**
 * Отримує ключ S3 з повного URL або повертає ключ як є
 * @param {string} imageUrl - S3 ключ або URL
 * @returns {string} - S3 ключ
 */
export const getS3Key = (imageUrl) => {
  if (!imageUrl) return '';
  if (!imageUrl.startsWith('http')) return imageUrl;

  try {
    const pathname = new URL(imageUrl).pathname.replace(/^\/+/, '');
    return pathname;
  } catch (e) {
    return imageUrl;
  }
};

/**
 * Генерує URL керованого ресайзу через прямий S3 CDN
 * @param {string} imageUrl - S3 ключ або URL
 * @param {number} width - Бажана ширина у px
 * @returns {string} - URL варіанту
 */
export const getResizedImageUrl = (imageUrl, width = 480) => {
  if (!imageUrl) return DEFAULT_RECIPE_IMAGE;
  const raw = String(imageUrl).trim();
  if (!raw || raw === 'null' || raw === 'undefined') return DEFAULT_RECIPE_IMAGE;
  if (raw.includes('default')) return DEFAULT_RECIPE_IMAGE;

  let key = getS3Key(raw);
  if (!key) return DEFAULT_RECIPE_IMAGE;

  if (key.startsWith('_resized/')) {
    key = key.replace(/^_resized\//, '');
  }
  key = key.replace(/\.w\d+(\.[a-z0-9]+)?$/i, '');

  // Прямий S3 URL на оптимізований WebP варіант
  const dotIndex = key.lastIndexOf('.');
  const base = dotIndex === -1 ? key : key.slice(0, dotIndex);
  return `https://cookly-food.s3.eu-north-1.amazonaws.com/_resized/${base}.w${width || 480}.webp`;
};

/**
 * Avatar URL resolution. External avatars (e.g. Google) are returned as-is.
 * S3 avatars are served directly from S3 (original WebP) since avatar keys
 * are not resized or stored in _resized/.
 */
export const getAvatarImageUrl = (avatarUrl, width = 160) => {
  if (!avatarUrl) return DEFAULT_AVATAR;

  const value = String(avatarUrl).trim();
  if (!value || value === 'null' || value === 'undefined') {
    return DEFAULT_AVATAR;
  }

  // Зовнішні аватари (Google, Facebook тощо)
  if (/^https?:\/\//i.test(value) && !/amazonaws\.com/i.test(value)) {
    return value;
  }

  // Якщо це вже повний S3 URL — повертаємо як є, зберігаючи фактичне розширення
  if (/^https?:\/\/.*amazonaws\.com/i.test(value)) {
    return value;
  }

  // Дефолтний аватар
  if (value.includes('default')) {
    return DEFAULT_AVATAR;
  }

  return getFullS3Url(value) || DEFAULT_AVATAR;
};

/**
 * Генерує безпечний srcset з гарантовано існуючими варіантами у S3.
 * @param {string} imageUrl - S3 ключ або URL
 * @returns {string} - srcset рядок
 */
export const generateSrcSet = (imageUrl) => {
  if (!imageUrl) return '';
  const key = getS3Key(imageUrl);
  if (!key || key.includes('default')) return '';

  return SAFE_SRCSET_WIDTHS
    .map((width) => `${getResizedImageUrl(key, width)} ${width}w`)
    .join(', ');
};

/**
 * Генерує sizes атрибут для різних breakpoints
 * @param {string} type - Тип компонента ('card', 'detail', 'thumbnail')
 * @returns {string} - sizes рядок
 */
export const generateSizes = (type = 'card') => {
  const sizesMap = {
    card: '(max-width: 480px) 100vw, (max-width: 768px) 50vw, (max-width: 1200px) 33vw, 240px',
    detail: '(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px',
    thumbnail: '(max-width: 768px) 50px, 100px',
    hero: '100vw'
  };
  
  return sizesMap[type] || sizesMap.card;
};

/**
 * Отримує оптимальний розмір зображення для поточного viewport
 * @param {string} type - Тип компонента
 * @returns {string} - Суфікс розміру ('-small', '-medium', '-large', '')
 */
export const getOptimalImageSize = (type = 'card') => {
  const width = window.innerWidth;
  const dpr = window.devicePixelRatio || 1;
  const effectiveWidth = width * dpr;
  
  if (type === 'thumbnail') {
    return '-small';
  }
  
  if (type === 'card') {
    if (effectiveWidth <= 480) return '-small';
    if (effectiveWidth <= 1024) return '-medium';
    return '-medium';
  }
  
  if (type === 'detail') {
    if (effectiveWidth <= 768) return '-medium';
    if (effectiveWidth <= 1920) return '-large';
    return '';
  }
  
  return '-medium';
};

/**
 * Додає суфікс розміру до URL зображення
 * @param {string} imageUrl - Оригінальний URL
 * @param {string} suffix - Суфікс розміру
 * @returns {string} - URL з суфіксом
 */
export const addImageSizeSuffix = (imageUrl, suffix) => {
  return imageUrl;
};

/**
 * Отримує URL оптимального розміру для поточного контексту
 * @param {string} imageUrl - Оригінальний URL
 * @param {string} type - Тип компонента
 * @returns {string} - Оптимізований URL
 */
export const getOptimizedImageUrl = (imageUrl, type = 'card') => {
  if (!imageUrl) return DEFAULT_RECIPE_IMAGE;

  const widthMap = {
    thumbnail: 480,
    card: 480,
    detail: 768,
    hero: 768,
  };

  const width = widthMap[type] || widthMap.card;
  return getResizedImageUrl(imageUrl, width);
};

/**
 * Preload критичних зображень
 * @param {string} imageUrl - URL зображення
 * @param {string} type - Тип зображення
 */
export const preloadImage = (imageUrl, type = 'card') => {
  if (!imageUrl || typeof document === 'undefined') return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = getOptimizedImageUrl(imageUrl, type);
  link.imageSrcset = generateSrcSet(imageUrl);
  link.imageSizes = generateSizes(type);
  
  document.head.appendChild(link);
};

/**
 * Lazy load зображення з Intersection Observer
 * @param {HTMLImageElement} img - Елемент зображення
 * @param {Function} onLoad - Callback після завантаження
 */
export const lazyLoadImage = (img, onLoad) => {
  if (!img || typeof IntersectionObserver === 'undefined') {
    if (onLoad) onLoad();
    return;
  }
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const image = entry.target;
        const src = image.dataset.src;
        const srcset = image.dataset.srcset;
        
        if (src) {
          image.src = src;
        }
        if (srcset) {
          image.srcset = srcset;
        }
        
        image.onload = () => {
          image.classList.add('loaded');
          if (onLoad) onLoad();
        };
        
        observer.unobserve(image);
      }
    });
  }, {
    rootMargin: '50px 0px', // Завантажуємо трохи раніше
    threshold: 0.01
  });
  
  observer.observe(img);
};

export default {
  DEFAULT_RECIPE_IMAGE,
  DEFAULT_AVATAR,
  getFullS3Url,
  getS3Key,
  getResizedImageUrl,
  getAvatarImageUrl,
  generateSrcSet,
  generateSizes,
  getOptimalImageSize,
  addImageSizeSuffix,
  getOptimizedImageUrl,
  preloadImage,
  lazyLoadImage
};
